import { ClampText, Text } from "../../../../components/common/typography";
import type { SimpleTableColumn } from "../../../../components/common/simple-table";
import { Chip } from "../../../../components/common/chip";
import { Image } from "../../../../components/common/image";
import type { ProductDto, ProductRequestDto } from "../../../../api";
import { ProductRequestDtoStatusEnum } from "../../../../api";
import { Button } from "../../../../components/common/button";
import { getProductLifecycleStatus } from "./product-lifecycle.utils";
import { ProductLifecycleChip } from "./product-lifecycle";
import type { ProductLifecycleAction } from "../../hooks/useProductLifecycleActions";

const IMAGE_COLUMN_CLASS = "w-14 flex-none";

export function buildProductColumns(
  onLifecycleAction: (product: ProductDto, action: ProductLifecycleAction) => void,
  pendingReviewIds?: Set<number>,
): SimpleTableColumn<ProductDto>[] {
  return [
    {
      key: "image",
      label: "",
      className: IMAGE_COLUMN_CLASS,
      render: (p) => (
        <Image
          image={p.image ?? null}
          alt={p.displayName ?? p.name ?? "Product"}
          size="w-14 h-14"
          objectFit="cover"
        />
      ),
    },
    {
      key: "product",
      label: "Product",
      className: "flex-[2] min-w-0",
      render: (p) => (
        <div className="flex flex-col items-start gap-1 min-w-0">
          <ClampText
            variant="bodyBold"
            tone="dark"
            lines={2}
            title={p.displayName ?? p.name ?? undefined}
            className="break-words"
          >
            {p.displayName ?? p.name ?? "—"}
          </ClampText>
          {pendingReviewIds?.has(p.id) && (
            <Chip variant="inReview">Pending review</Chip>
          )}
        </div>
      ),
    },
    {
      key: "category",
      label: "Category",
      className: "flex-1",
      render: (p) => (
        <Text variant="body" tone="body">
          {p.category?.name ?? "—"}
        </Text>
      ),
    },
    {
      key: "price",
      label: "Price (QAR)",
      className: "flex-1",
      render: (p) => (
        <Text variant="body" tone="body">
          {(p.sellerPrice ?? p.listPrice ?? 0).toLocaleString()}
        </Text>
      ),
    },
    {
      key: "stock",
      label: "Stock",
      className: "flex-1",
      // ProductDto has no stock field — that lives on ProductVariantDto.supplierStock.
      // TODO: roll up variant stock per template (or expose a field on ProductDto)
      // and replace the dash.
      render: () => (
        <Text variant="body" tone="muted">
          —
        </Text>
      ),
    },
    {
      key: "status",
      label: "Status",
      className: "flex-1 flex justify-center",
      render: (p) => <ProductLifecycleChip product={p} />,
    },
    {
      key: "actions",
      label: "Actions",
      className: "flex-[1.5] justify-end",
      render: (p) => {
        const status = getProductLifecycleStatus(p);

        if (status === "live") {
          return (
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="secondaryDark"
                size="xs"
                className="!h-7 !px-3"
                onClick={(e) => {
                  e.stopPropagation();
                  onLifecycleAction(p, "inactive");
                }}
              >
                Inactive
              </Button>
              <Button
                variant="secondaryDanger"
                size="xs"
                className="!h-7 !px-3"
                onClick={(e) => {
                  e.stopPropagation();
                  onLifecycleAction(p, "discontinued");
                }}
              >
                Discontinue
              </Button>
            </div>
          );
        }

        return (
          <div className="flex items-center justify-end">
            <Button
              variant="secondaryDark"
              size="xs"
              className="!h-7 !px-3"
              onClick={(e) => {
                e.stopPropagation();
                onLifecycleAction(p, "reactivate");
              }}
            >
              Reactivate
            </Button>
          </div>
        );
      },
    },
  ];
}

function statusChipForRequest(row: ProductRequestDto) {
  if (row.isDraft) return <Chip variant="tertiary">Draft</Chip>;
  if (row.status === ProductRequestDtoStatusEnum.Approved)
    return <Chip variant="approved">Approved</Chip>;
  if (row.status === ProductRequestDtoStatusEnum.Rejected)
    return <Chip variant="rejected">Rejected</Chip>;
  return <Chip variant="inReview">In Review</Chip>;
}

export function buildRequestColumns(
  categoryLabel: (categoryId: number | null | undefined) => string | undefined,
): SimpleTableColumn<ProductRequestDto>[] {
  return [
    {
      key: "image",
      label: "",
      className: IMAGE_COLUMN_CLASS,
      render: (r) => (
        <Image
          image={r.image ?? null}
          alt={r.name ?? "Product request"}
          size="w-14 h-14"
          objectFit="cover"
        />
      ),
    },
    {
      key: "type",
      label: "Type",
      className: "flex-1",
      // ProductRequestDto doesn't currently distinguish "new" vs "edit" — every
      // record is a new-product request today. Add a discriminator on the BE
      // (e.g. `kind` enum) to surface "Product edit" rows.
      render: () => (
        <Text variant="body" tone="muted">
          New product
        </Text>
      ),
    },
    {
      key: "product",
      label: "Product",
      className: "flex-[1.5] min-w-0",
      render: (r) => (
        <ClampText
          variant="body"
          tone="dark"
          lines={2}
          title={r.name ?? undefined}
          className="break-words"
        >
          {r.name ?? "—"}
        </ClampText>
      ),
    },
    {
      key: "category",
      label: "Category",
      className: "flex-1 min-w-0",
      render: (r) => (
        <Text variant="body" tone="body">
          {categoryLabel(r.categoryId) ?? "—"}
        </Text>
      ),
    },
    {
      key: "date",
      label: "Date",
      className: "flex-1",
      render: (r) => (
        <Text variant="body" tone="dark">
          {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—"}
        </Text>
      ),
    },
    {
      key: "status",
      label: "Status",
      className: "flex-1 flex justify-center",
      render: (r) => statusChipForRequest(r),
    },
  ];
}
